import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@dopomogai/ui';
import { CheckCircle2 } from 'lucide-react';

export type ProcessStep = {
  phase: string;
  titleKey: string;
  textKey: string;
  icon: React.ReactNode;
  accentColor: 'violet' | 'blue' | 'emerald';
  outputLabel: string;
};

export type ProcessPipelineSectionProps = {
  titleKey?: string;
  subtitleKey?: string;
  steps: ProcessStep[];
  primaryCta?: { label: string; actionId: string };
  onAction?: (actionId: string) => void;
};

const colorMap = {
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-900', iconBg: 'bg-violet-100', badge: 'violet' },
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-900',   iconBg: 'bg-blue-100',   badge: 'blue' },
  emerald:{ bg: 'bg-emerald-50',border: 'border-emerald-200',text: 'text-emerald-900',iconBg: 'bg-emerald-100',badge: 'emerald' },
} as const;

export const ProcessPipelineSection: React.FC<ProcessPipelineSectionProps> = ({
  titleKey,
  subtitleKey,
  steps,
  primaryCta,
  onAction
}) => {
  const { t } = useTranslation('homepage');

  return (
    <section className="bg-white py-20 md:py-28 border-b-2 border-slate-200 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" 
           style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        
        <div className="text-center mb-16 md:mb-24 max-w-3xl mx-auto">
          <Badge text="The Roadmap" accentColor="gray" className="mb-4" />
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">
            {t(titleKey || 'process.title', 'From Audit to Autonomy')}
          </h2>
          <p className="text-lg text-slate-600 font-medium">
            {t(subtitleKey || 'process.subtitle', 'We build the machine before we turn it on. A predictable, 6-week deployment.')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative">
          
          {/* Connector Line (Desktop Only) */}
          <div className="hidden md:block absolute top-[3.5rem] left-[16%] right-[16%] h-1 bg-slate-200 -z-10" />

          {steps.map((step, i) => {
            const colors = colorMap[step.accentColor];
            const isLast = i === steps.length - 1;

            return (
              <div key={i} className="relative flex flex-col items-center text-center group">
                
                {/* Card */}
                <div className={clsx(
                  "w-full bg-white border-2 border-black rounded-2xl p-2 shadow-[6px_6px_0_0_rgb(17,24,39)] transition-transform duration-300 hover:-translate-y-2",
                  isLast && "shadow-[6px_6px_0_0_#10b981] border-emerald-600"
                )}>
                  <div className={clsx("h-full rounded-xl p-6 flex flex-col items-center", colors.bg)}>
                    
                    <div className="mb-6">
                      <span className={clsx("text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-black/10 bg-white", colors.text)}>
                        {step.phase}
                      </span>
                    </div>

                    <div className={clsx(
                      "w-16 h-16 rounded-full border-2 border-black flex items-center justify-center mb-6 shadow-sm",
                      colors.iconBg
                    )}>
                      {step.icon}
                    </div>

                    <h3 className="text-xl font-extrabold text-slate-900 mb-3">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-6 font-medium min-h-[3rem]">
                      {t(step.textKey)}
                    </p>

                    <div className="mt-auto w-full pt-4 border-t border-black/10">
                      <div className="flex items-center justify-center gap-2 text-xs font-bold text-slate-700">
                        <CheckCircle2 size={14} className={clsx(step.accentColor === 'emerald' ? "text-emerald-600" : "text-slate-400")} />
                        <span className="uppercase tracking-wider opacity-70">Deliverable</span>
                      </div>
                      <div className={clsx("mt-1 font-extrabold text-sm", colors.text)}>
                        {step.outputLabel}
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            );
          })}
        </div>

        {primaryCta && (
          <div className="mt-20 text-center">
            <Button 
              variant="secondary" 
              size="lg" 
              text={primaryCta.label} 
              onClick={() => onAction?.(primaryCta.actionId)}
              className="border-2"
            />
          </div>
        )}

      </div>
    </section>
  );
};